// components/ModalActivarOnt.jsx
// Verifica pago del mes + estado REAL de la ONT en el OLT, y según lo que
// encuentre ofrece ACTIVAR (si está desactivada y con pago) o DESACTIVAR
// (si está activa) — corrige el caso "cortada en sistema pero activa en OLT".
import React, { useEffect, useRef, useState } from "react";
import { authFetch } from "../utils/authFetch";
import "./ModalActivarOnt.css";

const PASOS_DIAG = [
  "Conectando al OLT…",
  "Leyendo estado de la ONT…",
  "Revisando pago del mes…",
];
const pasosAccion = (accion) =>
  accion === "liberar"
    ? [
        "Localizando ONT por serial…",
        "Eliminando en el OLT…",
        "Verificando eliminación…",
        "Limpiando sistema…",
      ]
    : [
        "Conectando al OLT…",
        accion === "activar" ? "Enviando activación…" : "Enviando desactivación…",
        "Verificando en el OLT…",
        "Actualizando sistema…",
      ];

function rolActual() {
  try {
    return (JSON.parse(localStorage.getItem("user") || "null") || {}).rol || "";
  } catch {
    return "";
  }
}

export default function ModalActivarOnt({ cliente, onClose, onDone }) {
  const [fase, setFase] = useState("diagnosticando"); // diagnosticando | listo | trabajando | resultado
  const [diag, setDiag] = useState(null);
  const [error, setError] = useState("");
  const [resultado, setResultado] = useState(null);
  const [accionActual, setAccionActual] = useState(null); // "activar" | "desactivar" | "liberar"
  const [confirmar, setConfirmar] = useState(null); // panel de confirmación con diseño
  const [paso, setPaso] = useState(0);
  const timerRef = useRef(null);

  // Pasos a mostrar según la fase
  const pasos =
    fase === "trabajando" ? pasosAccion(accionActual)
    : fase === "diagnosticando" ? PASOS_DIAG
    : [];

  // Avanza los pasos con un timer mientras se espera al OLT.
  // Se detiene en el último paso (sigue "latiendo") hasta que llega la respuesta.
  useEffect(() => {
    clearInterval(timerRef.current);
    if (fase === "diagnosticando" || fase === "trabajando") {
      setPaso(0);
      const total = (fase === "trabajando" ? pasosAccion(accionActual) : PASOS_DIAG).length;
      timerRef.current = setInterval(() => {
        setPaso((p) => (p < total - 1 ? p + 1 : p));
      }, 1400);
    }
    return () => clearInterval(timerRef.current);
  }, [fase, accionActual]);

  useEffect(() => {
    let cancelado = false;
    async function diagnosticar() {
      try {
        const res = await authFetch(`/api/clientes/${cliente.id}/diagnostico_activacion`);
        const data = await res.json();
        if (cancelado) return;
        if (data.ok) {
          setDiag(data);
          setFase("listo");
        } else {
          setError(data.error || "No se pudo consultar el estado");
          setFase("listo");
        }
      } catch {
        if (!cancelado) {
          setError("Error de red consultando el diagnóstico");
          setFase("listo");
        }
      }
    }
    diagnosticar();
    return () => { cancelado = true; };
  }, [cliente.id]);

  async function ejecutar(accion) {
    setAccionActual(accion);
    setFase("trabajando");
    setError("");
    try {
      const url =
        accion === "activar" ? `/api/clientes/${cliente.id}/activar_ont`
        : accion === "liberar" ? `/api/clientes/${cliente.id}/liberar_ont`
        : `/api/clientes/${cliente.id}/desactivar_ont`;
      const res = await authFetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      const data = await res.json();
      if (data.ok) {
        setResultado(data.mensaje || "Operación realizada.");
        setFase("resultado");
        if (onDone) onDone();
      } else {
        setError(data.error || "No se pudo completar la operación");
        setFase("listo");
      }
    } catch {
      setError("Error de red ejecutando la operación");
      setFase("listo");
    }
  }

  const ontFlag = diag?.ont?.control_flag;
  const runState = diag?.ont?.run_state;
  const enLinea = runState === "online";
  // Sin señal solo preocupa si la ONT está autorizada: si fue desactivada
  // por corte, que no reporte es justo lo esperado y no debe alarmar.
  const sinSenalRelevante = !!runState && !enLinea && ontFlag === "active";
  const pagado = diag?.pago_mes?.realizado;
  const enProceso = fase === "diagnosticando" || fase === "trabajando";

  return (
    <div className="mao-overlay" onClick={enProceso ? undefined : onClose}>
      <div className="mao-modal" onClick={(e) => e.stopPropagation()}>
        <h3>Verificar ONT</h3>
        <p className="mao-cliente">
          #{cliente.id} · {cliente.nombre} {cliente.apellidos}
        </p>

        {/* ---------- ANIMACIÓN: spinner + pasos ---------- */}
        {enProceso && (
          <div className="mao-proceso">
            <div className="mao-spinner" />
            <ul className="mao-pasos">
              {pasos.map((texto, i) => {
                const estado = i < paso ? "hecho" : i === paso ? "actual" : "pend";
                return (
                  <li key={i} className={`mao-paso ${estado}`}>
                    <span className="mao-paso-ic">
                      {estado === "hecho" ? "✓" : estado === "actual" ? "" : ""}
                    </span>
                    {texto}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {!enProceso && diag && fase !== "resultado" && (
          <div className="mao-diag">
            <div className={`mao-linea ${pagado ? "ok" : "warn"}`}>
              {pagado ? "✅" : "❌"} Pago {diag.pago_mes?.texto}:{" "}
              <strong>{pagado ? "realizado" : "sin registrar"}</strong>
            </div>
            {/* Autorización en el OLT: lo que SÍ controla la app (control flag) */}
            <div className={`mao-linea ${ontFlag === "active" ? "ok" : ontFlag === "deactive" ? "warn" : ""}`}>
              {diag.ont?.existe === false ? "🚫"
                : ontFlag === "active" ? "✅"
                : ontFlag === "deactive" ? "⛔" : "❓"} Autorización en el OLT:{" "}
              <strong>
                {diag.ont?.existe === false ? "no existe en el OLT"
                  : ontFlag === "active" ? "activa"
                  : ontFlag === "deactive" ? "desactivada"
                  : "estado desconocido"}
              </strong>
            </div>

            {/* Conexión (run state): va en línea APARTE a propósito.
                Antes se mostraba como "(offline)" pegado al ✅ de la
                autorización, y se leía como "todo bien" cuando el equipo
                en realidad no estaba reportando. Son dos cosas distintas:
                la autorización la decide la app, la conexión depende del
                equipo del cliente y de la fibra. */}
            {diag.ont?.existe !== false && (
              <div className={`mao-linea ${enLinea ? "ok" : sinSenalRelevante ? "warn" : ""}`}>
                {enLinea ? "🟢" : runState ? "🔴" : "❓"} Conexión:{" "}
                <strong>
                  {enLinea ? "ONT en línea"
                    : runState === "offline" ? "ONT fuera de línea"
                    : runState || "desconocida"}
                </strong>
                {sinSenalRelevante && (
                  <div className="mao-nota">
                    Equipo apagado, falla en su instalación o ONT dañada.
                  </div>
                )}
              </div>
            )}
            {diag.ont?.serial && (
              <div className="mao-linea">
                🏷️ Serial: <strong>{diag.ont.serial}</strong>
              </div>
            )}
            {diag.ont?.ajena && (
              <div className="mao-linea warn">
                ⚠️ <strong>Esta ONT ya es de otro cliente</strong> (reinstalada)
              </div>
            )}
            {/* Historial del periodo, NO el estado actual del servicio. Decía
                "Corte registrado este mes" y se leía como "está cortado". */}
            {diag.corte_registrado && (
              <div className="mao-linea">
                🗒️ Hubo un corte este mes: <strong>sí</strong>
                {pagado && (
                  <div className="mao-nota">
                    Ya pagó después del corte: es historial del periodo, no el estado actual.
                  </div>
                )}
              </div>
            )}
            <p className="mao-mensaje">{diag.mensaje}</p>
          </div>
        )}

        {/* Depuración: salida cruda del OLT (si el estado sale desconocido) */}
        {!enProceso && diag?.raw_ont && !ontFlag && (
          <details className="mao-raw">
            <summary>Ver salida del OLT</summary>
            <pre>{diag.raw_ont}</pre>
          </details>
        )}

        {error && <p className="mao-error">{error}</p>}
        {fase === "resultado" && <p className="mao-exito">✓ {resultado}</p>}

        {/* ---------- PANEL DE CONFIRMACIÓN (con diseño, sin alerts) ---------- */}
        {fase === "listo" && confirmar && (
          <div className={`mao-confirm ${confirmar === "liberar" ? "rojo" : "ambar"}`}>
            <h4>
              {confirmar === "liberar"
                ? "🗑 Liberar ONT y cancelar cliente"
                : "⚠️ Desactivar con pago vigente"}
            </h4>
            {confirmar === "liberar" ? (
              <ul>
                <li>Elimina la ONT del OLT (queda libre para reciclar)</li>
                <li>Borra su registro de ONU del sistema</li>
                <li>
                  Marca a <strong>#{cliente.id} {cliente.nombre} {cliente.apellidos}</strong>{" "}
                  como <strong>CANCELADO</strong>
                </li>
              </ul>
            ) : (
              <p>
                Este cliente <strong>tiene pago del mes</strong>. Si continúas,
                se le cortará el servicio.
              </p>
            )}
            <p className="mao-confirm-aviso">Esta acción no se puede deshacer.</p>
          </div>
        )}

        <div className="mao-botones">
          {fase === "listo" && confirmar ? (
            <>
              <button
                className={confirmar === "liberar" ? "mao-liberar" : "mao-desactivar"}
                onClick={() => { const a = confirmar; setConfirmar(null); ejecutar(a); }}
              >
                {confirmar === "liberar" ? "Sí, liberar y cancelar" : "Sí, desactivar"}
              </button>
              <button className="mao-cerrar" onClick={() => setConfirmar(null)}>
                Volver
              </button>
            </>
          ) : (
            <>
              {fase === "listo" && diag?.puede_activar && (
                <button className="mao-activar" onClick={() => ejecutar("activar")}>
                  Activar ONT
                </button>
              )}
              {fase === "listo" && diag?.puede_desactivar && (
                <button
                  className="mao-desactivar"
                  onClick={() => (pagado ? setConfirmar("desactivar") : ejecutar("desactivar"))}
                >
                  Desactivar ONT
                </button>
              )}
              {fase === "listo" && diag && !diag.ont?.ajena &&
                ["admin", "caja"].includes(rolActual()) && (
                <button className="mao-liberar" onClick={() => setConfirmar("liberar")}>
                  🗑 Liberar ONT (cancelar)
                </button>
              )}
              <button className="mao-cerrar" onClick={onClose} disabled={enProceso}>
                {fase === "resultado" ? "Cerrar" : "Cancelar"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
